import useModal from '@area2k/use-modal';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { addDays, parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import SuccessfulAlertModal from '../../Reports/shared/SuccessfulAlertModal';
import ItemPayrollDetails from '../../Payrolls/ItemPayroll';
import PageSkeleton from '../../Payrolls/Skeletons/DetailsSkeleton';
import ErrorPage from '../../Payrolls/Skeletons/ErrorPage';

import ConfirmApprovalPayrollModal from './ConfirmApprovalPayrollModal';
import ConfirmDeletePayrollModal from './ConfirmDeletePayrollModal';
import ContractorPaymentsCard from '../../Payrolls/PayrollDetail/ContractorPaymentsCard';
import DetailsCard from '../../Payrolls/PayrollDetail/DetailsCard';
import ItemsCard from '../../Payrolls/PayrollDetail/ItemsCard';
import TotalsCard from '../../Payrolls/PayrollDetail/TotalsCard';

import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Prettify } from '@/types';
import {
  PayrollStatusEnum,
  SimpleContractorPaymentsFragment,
  SimplePayrollItemFragment,
  Scalars,
  Maybe,
} from '@/types/graphql';
import { formatHumanDate } from '@/util/datetime';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { Payroll } from '../../Payrolls/PayrollDetail';
import { format } from 'date-fns';
import { PLANE_DATE } from '@/constants/datetime';

export type ItemSelected = {
  showItem: Scalars['Boolean'];
  item: Prettify<
    Maybe<
      | Prettify<SimplePayrollItemFragment>
      | Prettify<SimpleContractorPaymentsFragment>
    >
  >;
  type: Maybe<'item' | 'contractor'>;
};

// Helper function to convert ISO8601 datetime to date-only string
const isoToDateString = (isoString: string | null): string | null => {
  if (!isoString) return null;
  try {
    const date = parseISO(isoString);
    return format(date, PLANE_DATE);
  } catch (e) {
    // If parsing fails, try to extract date part manually
    return isoString.split('T')[0];
  }
};

// Transform payroll item from API to match GraphQL structure
const transformPayrollItem = (item: any, index: number) => {
  // Log the raw item to help debug
  if (index === 0) {
    console.log('Raw payroll item from API:', item);
    console.log('Item worker:', item.worker);
    console.log(
      'Item worker keys:',
      item.worker ? Object.keys(item.worker) : 'null',
    );
  }

  // Try multiple possible structures for worker data
  let workerData = null;

  if (item.worker) {
    // Check if worker has nested user object
    if (item.worker.user) {
      workerData = {
        user: {
          id: item.worker.user.id?.toString() || item.worker.user.id || '',
          firstName:
            item.worker.user.first_name ||
            item.worker.user.firstName ||
            item.worker.user.firstname ||
            '',
          middleName:
            item.worker.user.middle_name ||
            item.worker.user.middleName ||
            item.worker.user.middlename ||
            null,
          lastName:
            item.worker.user.last_name ||
            item.worker.user.lastName ||
            item.worker.user.lastname ||
            '',
        },
      };
    }
    // Check if worker has flat user fields
    else if (
      item.worker.first_name ||
      item.worker.firstName ||
      item.worker.firstname
    ) {
      workerData = {
        user: {
          id:
            item.worker.id?.toString() || item.worker.user_id?.toString() || '',
          firstName:
            item.worker.first_name ||
            item.worker.firstName ||
            item.worker.firstname ||
            '',
          middleName:
            item.worker.middle_name ||
            item.worker.middleName ||
            item.worker.middlename ||
            null,
          lastName:
            item.worker.last_name ||
            item.worker.lastName ||
            item.worker.lastname ||
            '',
        },
      };
    }
    // Check if there's a worker_user nested object
    else if (item.worker_user) {
      workerData = {
        user: {
          id: item.worker_user.id?.toString() || '',
          firstName:
            item.worker_user.first_name || item.worker_user.firstName || '',
          middleName:
            item.worker_user.middle_name || item.worker_user.middleName || null,
          lastName:
            item.worker_user.last_name || item.worker_user.lastName || '',
        },
      };
    }
  }

  if (index === 0) {
    console.log('Transformed worker data:', workerData);
  }

  return {
    id: item.id?.toString() || item.id,
    netPay: item.net_pay || item.netPay || null,
    paymentMethod: item.payment_method || item.paymentMethod || '',
    status: (item.status?.toUpperCase() || 'DRAFT') as any,
    worker: workerData,
    earnings: item.earnings || [],
    taxes: item.taxes || [],
    deductions: item.deductions || null,
  };
};

// Transform contractor payment from API to match GraphQL structure
const transformContractorPayment = (item: any, index: number) => {
  // Log the raw item to help debug
  if (index === 0) {
    console.log(
      'Raw contractor payment from API:',
      JSON.stringify(item, null, 2),
    );
    console.log('Contractor payment worker:', item.worker);
    if (item.worker) {
      console.log('Contractor payment worker keys:', Object.keys(item.worker));
      console.log('Contractor payment worker values:', item.worker);
      if (item.worker.user) {
        console.log(
          'Contractor payment worker.user keys:',
          Object.keys(item.worker.user),
        );
        console.log('Contractor payment worker.user values:', item.worker.user);
      }
    }
  }

  // Try multiple possible structures for worker data
  let workerData: {
    user: { firstName: string; middleName: string | null; lastName: string };
  } | null = null;

  if (item.worker) {
    // Check if worker has nested user object (most common case - matches GraphQL structure)
    if (item.worker.user) {
      const firstName =
        item.worker.user.first_name ||
        item.worker.user.firstName ||
        item.worker.user.firstname ||
        '';
      const lastName =
        item.worker.user.last_name ||
        item.worker.user.lastName ||
        item.worker.user.lastname ||
        '';

      // Only create workerData if we have at least a first or last name
      if (firstName || lastName) {
        workerData = {
          user: {
            firstName,
            middleName:
              item.worker.user.middle_name ||
              item.worker.user.middleName ||
              item.worker.user.middlename ||
              null,
            lastName,
          },
        };
      }
    }
    // Check if worker has flat user fields (user data directly on worker)
    else if (
      item.worker.first_name ||
      item.worker.firstName ||
      item.worker.firstname ||
      item.worker.last_name ||
      item.worker.lastName ||
      item.worker.lastname
    ) {
      const firstName =
        item.worker.first_name ||
        item.worker.firstName ||
        item.worker.firstname ||
        '';
      const lastName =
        item.worker.last_name ||
        item.worker.lastName ||
        item.worker.lastname ||
        '';

      if (firstName || lastName) {
        workerData = {
          user: {
            firstName,
            middleName:
              item.worker.middle_name ||
              item.worker.middleName ||
              item.worker.middlename ||
              null,
            lastName,
          },
        };
      }
    }
  }
  // Check if there's a worker_user nested object at the top level
  else if (item.worker_user) {
    const firstName =
      item.worker_user.first_name || item.worker_user.firstName || '';
    const lastName =
      item.worker_user.last_name || item.worker_user.lastName || '';

    if (firstName || lastName) {
      workerData = {
        user: {
          firstName,
          middleName:
            item.worker_user.middle_name || item.worker_user.middleName || null,
          lastName,
        },
      };
    }
  }

  if (index === 0) {
    console.log('Transformed contractor payment worker data:', workerData);
  }

  return {
    id: item.id?.toString() || item.id,
    netPay: item.net_pay || item.netPay || null,
    paymentMethod: item.payment_method || item.paymentMethod || '',
    status: (item.status?.toUpperCase() || 'DRAFT') as any,
    worker: workerData,
    contractorPaymentItems:
      item.contractor_payment_items || item.contractorPaymentItems || [],
  };
};

// Transform REST API response to match GraphQL structure
const transformPayrollData = (apiData: any): Payroll => {
  return {
    id: apiData.id.toString(),
    approvalDeadline: apiData.approval_deadline,
    approvedAt: apiData.approved_at,
    payday: isoToDateString(apiData.payday) || apiData.payday,
    periodEnd: isoToDateString(apiData.period_end) || apiData.period_end,
    periodStart: isoToDateString(apiData.period_start) || apiData.period_start,
    status: apiData.status.toUpperCase() as PayrollStatusEnum,
    payrollItems: {
      items: (apiData.payroll_items || []).map((item: any, index: number) =>
        transformPayrollItem(item, index),
      ),
    },
    contractorPayments: {
      items: (apiData.contractor_payments || []).map(
        (item: any, index: number) => transformContractorPayment(item, index),
      ),
    },
    totals: apiData.totals
      ? {
          // Transform snake_case to camelCase and ensure values are strings
          companyTaxes:
            apiData.totals.company_taxes?.toString() ||
            apiData.totals.companyTaxes?.toString() ||
            '0',
          companyBenefits:
            apiData.totals.company_benefits?.toString() ||
            apiData.totals.companyBenefits?.toString() ||
            '0',
          liability:
            apiData.totals.liability?.toString() ||
            apiData.totals.liability?.toString() ||
            '0',
          cashRequirement:
            apiData.totals.cash_requirement?.toString() ||
            apiData.totals.cashRequirement?.toString() ||
            '0',
          employeeGross:
            apiData.totals.employee_gross?.toString() ||
            apiData.totals.employeeGross?.toString() ||
            '0',
          employeeTaxes:
            apiData.totals.employee_taxes?.toString() ||
            apiData.totals.employeeTaxes?.toString() ||
            '0',
          postTaxDeductions:
            apiData.totals.post_tax_deductions?.toString() ||
            apiData.totals.postTaxDeductions?.toString() ||
            '0',
          employeeBenefits:
            apiData.totals.employee_benefits?.toString() ||
            apiData.totals.employeeBenefits?.toString() ||
            '0',
          employeeNet:
            apiData.totals.employee_net?.toString() ||
            apiData.totals.employeeNet?.toString() ||
            '0',
          employeeReimbursements:
            apiData.totals.employee_reimbursements?.toString() ||
            apiData.totals.employeeReimbursements?.toString() ||
            '0',
        }
      : {
          companyTaxes: '0',
          companyBenefits: '0',
          liability: '0',
          cashRequirement: '0',
          employeeGross: '0',
          employeeTaxes: '0',
          postTaxDeductions: '0',
          employeeBenefits: '0',
          employeeNet: '0',
          employeeReimbursements: '0',
        },
  };
};

const PayrollDetailV2 = () => {
  const { payrollId } = useParams();
  const [payroll, setPayroll] = useState<Payroll | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [item, setItem] = useState<ItemSelected>({
    showItem: false,
    item: null,
    type: null,
  });

  const fetchPayrollDetail = useCallback(async () => {
    if (!payrollId) {
      setError('Payroll ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axiosRubyClient.get(`/api/payrolls/${payrollId}`);

      if (response.data?.success && response.data?.data?.payroll) {
        console.log('Payroll API Response:', response.data.data.payroll);
        console.log('Totals from API:', response.data.data.payroll.totals);
        console.log(
          'Payroll Items from API:',
          response.data.data.payroll.payroll_items,
        );
        if (response.data.data.payroll.payroll_items?.length > 0) {
          console.log(
            'First Payroll Item:',
            response.data.data.payroll.payroll_items[0],
          );
          console.log(
            'First Item Worker:',
            response.data.data.payroll.payroll_items[0]?.worker,
          );
        }
        console.log(
          'Contractor Payments from API:',
          response.data.data.payroll.contractor_payments,
        );
        if (response.data.data.payroll.contractor_payments?.length > 0) {
          console.log(
            'First Contractor Payment:',
            response.data.data.payroll.contractor_payments[0],
          );
          console.log(
            'First Contractor Payment Worker:',
            response.data.data.payroll.contractor_payments[0]?.worker,
          );
        }
        const transformedData = transformPayrollData(
          response.data.data.payroll,
        );
        console.log('Transformed Totals:', transformedData.totals);
        console.log(
          'Transformed Payroll Items:',
          transformedData.payrollItems.items,
        );
        if (transformedData.payrollItems.items?.length > 0) {
          console.log(
            'First Transformed Item:',
            transformedData.payrollItems.items[0],
          );
          console.log(
            'First Transformed Item Worker:',
            transformedData.payrollItems.items[0]?.worker,
          );
        }
        console.log(
          'Transformed Contractor Payments:',
          transformedData.contractorPayments.items,
        );
        if (transformedData.contractorPayments.items?.length > 0) {
          console.log(
            'First Transformed Contractor Payment:',
            transformedData.contractorPayments.items[0],
          );
          console.log(
            'First Transformed Contractor Payment Worker:',
            transformedData.contractorPayments.items[0]?.worker,
          );
        }
        setPayroll(transformedData);
      } else {
        setError('Failed to load payroll data');
      }
    } catch (err: any) {
      console.error('Error fetching payroll detail:', err);
      setError(err.response?.data?.message || 'Failed to load payroll');
    } finally {
      setLoading(false);
    }
  }, [payrollId]);

  useEffect(() => {
    fetchPayrollDetail();
  }, [fetchPayrollDetail]);

  const handleDownloadPayroll = async () => {
    if (!payrollId) return;

    setDownloadLoading(true);
    try {
      const response = await axiosRubyClient.get(
        `/api/payrolls/${payrollId}/journal_report`,
        { responseType: 'blob' },
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll-${payrollId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccessfulAlertModal();
    } catch (err) {
      console.error('Error downloading payroll:', err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const [showConfirmApprovalModal, hideConfirmApprovalModal] = useModal(
    () => (
      <ConfirmApprovalPayrollModal
        hideModal={hideConfirmApprovalModal}
        payrollId={payrollId || ''}
        onSuccess={fetchPayrollDetail}
      />
    ),
    [payrollId, fetchPayrollDetail],
  );
  const [showConfirmDeleteModal, hideConfirmDeleteModal] = useModal(
    () => (
      <ConfirmDeletePayrollModal
        hideModal={hideConfirmDeleteModal}
        payrollId={payrollId || ''}
      />
    ),
    [payrollId],
  );

  const [showSuccessfulAlertModal, hideSuccessfulAlertModal] = useModal(
    () => <SuccessfulAlertModal hideModal={hideSuccessfulAlertModal} />,
    [],
  );

  if (error) {
    return <ErrorPage />;
  }

  if (loading || !payroll) {
    return <PageSkeleton />;
  }

  const formattedPayday = formatHumanDate(addDays(parseISO(payroll.payday), 1));

  return !item.showItem ? (
    <Page
      primaryAction={
        payroll.status === PayrollStatusEnum.DRAFT
          ? {
              a11yLabel: 'Approve Preview Payroll',
              id: 'approve-payroll-btn',
              label: 'Approve Payroll',
              onAction: showConfirmApprovalModal,
            }
          : undefined
      }
      secondaryActions={[
        {
          a11yLabel: 'Download Payroll',
          label: 'Download Payroll',
          onAction: handleDownloadPayroll,
          id: 'download-payroll-btn',
          isLoading: downloadLoading,
          icon: faDownload,
          disabled: downloadLoading,
        },
        ...(payroll.status === PayrollStatusEnum.DRAFT
          ? [
              {
                a11yLabel: 'Delete Payroll',
                label: 'Delete Payroll',
                dangerous: true,
                onAction: showConfirmDeleteModal,
                id: 'btn-delete-payroll',
              },
            ]
          : []),
      ]}
      size="lg"
      title={`Payroll Journal ${formattedPayday}`}
    >
      <Stack vertical gap={30}>
        <DetailsCard payroll={payroll} />
        <TotalsCard totals={payroll.totals} />
        {payroll.payrollItems && payroll.payrollItems.items!.length > 0 && (
          <ItemsCard items={payroll.payrollItems?.items} setItem={setItem} />
        )}
        {payroll.contractorPayments &&
          payroll.contractorPayments.items.length > 0 && (
            <ContractorPaymentsCard
              items={payroll.contractorPayments.items}
              setItem={setItem}
            />
          )}
      </Stack>
    </Page>
  ) : (
    <ItemPayrollDetails
      context={item.type ?? 'item'}
      item={item.item}
      setItem={setItem}
    />
  );
};

export default PayrollDetailV2;
